import { ChangeEvent, useState } from 'react';
import { Icon as BlueIcon } from '@blueprintjs/core';

import ReactDom from 'react-dom';

import * as yup from 'yup';
import BackIconImage from '@assets/svg/back-icon.svg';
import AvatarDefaultImg from '@assets/svg/avatar-placeholder.svg';

import { faTimes } from '@fortawesome/free-solid-svg-icons';
import { useMutation, useQueryClient } from 'react-query';
import { patchUsers } from '@services/queries/users/patchUsers/patchUsers';
import { Controller, useForm } from 'react-hook-form';
import { IUser } from '@models/User.model';
import { yupResolver } from '@hookform/resolvers/yup';
import { CustomPicker } from '@components/CustomPickerWithBorder/CustomPicker';

import { extractUrl } from '@utils/updateBeamUrl/updateBeamUrl';
import {
  Container,
  Overlay,
  Close,
  Header,
  Title,
  Back,
  Confirm,
  ProfileImg,
  Row,
  Column,
  Label,
  Line,
  FileName,
  IconCloseAwesome,
  Input,
  SelectFile,
  Form,
  Divider,
  SpacingDivider,
} from './EditUserDetails.styles';

interface IEditUserDetails {
  open: boolean;
  onClose: () => void;
  user: IUser;
}

interface IFormData {
  name: string;
  email: string;
  picture: string;
  role: string;
  file?: FileList;
}

const options = [
  { value: 'admin', label: 'Admin' },
  { value: 'editor', label: 'Editor' },
  { value: 'viewer', label: 'Viewer' },
  { value: 'member', label: 'Member' },
];

export const EditUserDetails = ({ open, onClose, user }: IEditUserDetails) => {
  const portalDiv = document.getElementById('portal');
  const queryClient = useQueryClient();

  const [edited, setEdited] = useState<boolean>(false);
  const [topic, setTopic] = useState<'user' | null>(null);
  const [fileName, setFileName] = useState<string>('');
  const [previewFilePicture, setPreviewFilePicture] = useState<string>('');

  const schema = yup
    .object({
      name: yup.string(),
      email: yup.string(),
      role: yup.string(),
      picture: yup.string(),
      file: yup.mixed(),
    })
    .required();

  const mutationEditUser = useMutation(patchUsers, {
    onSuccess: () => {
      onClose();
      queryClient.invalidateQueries(['membersAuth0']);
    },
  });

  const {
    register,
    handleSubmit,
    formState: { isValid },
    setValue,
    control,
  } = useForm<IFormData>({
    resolver: yupResolver(schema),
    defaultValues: {
      name: user.name || '',
      email: user.email || '',
      role: user.role || '',
      picture: user.picture || '',
    },
    mode: 'onChange',
  });

  const onSubmit = (data: IFormData) => {
    const result: any = {
      id: user.id,
      name: data.name,
      email: data.email,
      picture: extractUrl(data.picture),
      file: data.file?.[0],
      role: data.role,
    };
    if (!mutationEditUser.isLoading) {
      mutationEditUser.mutate(result);
    }
  };

  const previewFileImage = (e: ChangeEvent<HTMLInputElement>) => {
    const imagePreviewFileUrl = e.target.files;
    if (imagePreviewFileUrl && imagePreviewFileUrl.length > 0) {
      const imageSrc = URL.createObjectURL(imagePreviewFileUrl[0]);
      setPreviewFilePicture(imageSrc);
    }
  };

  const ProfileImage = () => {
    if (previewFilePicture.length) return previewFilePicture;
    if (user.picture) return user.picture;
    return AvatarDefaultImg;
  };

  const Logo = () => {
    if (fileName !== '')
      return (
        <FileName>
          {fileName}
          <IconCloseAwesome
            icon={faTimes}
            onClick={() => {
              setFileName('');
              setValue('file', undefined);
              setPreviewFilePicture('');
            }}
          />
        </FileName>
      );
    return (
      <div>
        <SelectFile htmlFor="upload">Select File</SelectFile>
        <input
          type="file"
          id="upload"
          hidden
          accept="image/*"
          {...register('file')}
          onChange={e => {
            register('file').onChange(e);
            setFileName(e.target.files?.[0]?.name || '');
            setEdited(true);
            previewFileImage(e);
          }}
        />
      </div>
    );
  };

  return open && portalDiv
    ? ReactDom.createPortal(
        <Overlay>
          <Container>
            <Header>
              {topic && (
                <>
                  <Back
                    src={BackIconImage}
                    alt="back"
                    onClick={() => setTopic(null)}
                  />
                  <SpacingDivider />
                </>
              )}
              <Title>Edit User</Title>
              <Close
                onClick={() => {
                  onClose();
                  setPreviewFilePicture('');
                }}
              >
                <BlueIcon icon="cross" color="#8F95B2" />
              </Close>
            </Header>
            <Divider />
            <Form onSubmit={handleSubmit(onSubmit)}>
              <Row>
                <ProfileImg src={ProfileImage()} alt="Profile Img" />

                <Column>
                  <Label className="picture">Picture</Label>
                  {Logo()}
                </Column>
              </Row>

              <Label>Name</Label>

              <Input
                type="text"
                placeholder="John Doe"
                {...register('name')}
                onChange={() => {
                  setEdited(true);
                }}
                className="infoInputs"
              />

              <Label>Email</Label>

              <Input
                type="text"
                placeholder="John Doe"
                {...register('email')}
                onChange={() => {
                  setEdited(true);
                }}
                className="infoInputs"
              />

              <Label>Role</Label>

              <Controller
                control={control}
                defaultValue={user.role}
                name="role"
                render={({ field: { onChange, value } }) => (
                  <CustomPicker
                    options={options}
                    value={options.find(c => c.value === value)}
                    onChange={(val: any) => {
                      onChange(val.value);
                      setEdited(true);
                    }}
                    placeholder="Select studio type"
                  />
                )}
              />

              <Line>
                <Confirm disabled={!isValid || !edited} type="submit">
                  Save
                </Confirm>
              </Line>
            </Form>
          </Container>
        </Overlay>,
        portalDiv,
      )
    : null;
};
